import React, {useReducer} from 'react'
import {useDispatch} from 'react-redux'
import {styled} from '@material-ui/core/styles'
import {Link} from 'react-router-dom'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import {useRegistrationState} from 'users/hooks'
import {createUser} from 'users/store/actions'
import CountrySelect from 'ui/components/CountrySelect'
import {initializeState, reducer, updateField, showError} from './state'
import Legal from './Legal'
import {
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  MenuItem as MuiMenuItem,
  Select as MuiSelect
} from '@material-ui/core'
import FormHelperText from '@material-ui/core/FormHelperText'

const fields = [
  {
    key: 'firstName',
    label: 'First name',
    type: 'text',
    required: true,
    autocomplete: 'given-name'
  },
  {
    key: 'lastName',
    label: 'Last name',
    type: 'text',
    required: true,
    autocomplete: 'family-name'
  },
  {
    key: 'jobTitle',
    label: 'Job Title',
    type: 'text',
    required: true,
    autocomplete: 'organization-title'
  },
  {
    key: 'phone',
    label: 'Phone',
    type: 'tel',
    required: true,
    autocomplete: 'tel'
  },
  {
    key: 'email',
    label: 'Email',
    type: 'email',
    required: true,
    autocomplete: 'email'
  },
  {
    key: 'companyName',
    label: 'Company Name',
    type: 'text',
    required: true,
    autocomplete: 'organization'
  },
  {
    key: 'organizationType',
    label: 'Organization Type',
    type: 'organizationType',
    required: true,
    autocomplete: 'organization-type'
  },
  {
    key: 'city',
    label: 'City',
    type: 'text',
    required: true,
    autocomplete: 'address-level2'
  },
  {
    key: 'state',
    label: 'State/Province',
    type: 'text',
    required: true,
    autocomplete: 'address-level1'
  },
  {
    key: 'country',
    label: 'Country',
    type: 'countrySelect',
    required: true,
    autocomplete: 'country-name'
  },
  {
    key: 'usageType',
    label: 'How do you plan to use FindAnLMS?',
    type: 'usageType',
    required: true,
    autocomplete: 'usage-type'
  },
  /*{
    key: 'contactConsent',
    label: 'May we contact you for your perspective?',
    type: 'contactConsent',
    autocomplete: 'contact-consent'
  },*/
  {
    key: 'purchaseLearningSystem',
    label: 'When do you plan to purchase a learning system?',
    type: 'purchaseLearningSystem',
    required: true,
    autocomplete: 'purchase-learning-system'
  },
  {
    key: 'numberOfUsersToSupport',
    label: 'Number of users to support',
    type: 'numberOfUsersToSupport',
    required: true,
    autocomplete: 'number-of-users-to-support'
  }
]

const Content = styled('div')({})

const Title = styled('h2')({
  fontSize: 24,
  marginTop: 12,
  marginBottom: 6
})

const Subtitle = styled('h4')({
  fontSize: 16,
  fontWeight: 'normal',
  marginTop: 0,
  marginBottom: 18
})

const Form = styled('form')(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 8px 1fr',

  // default text fields
  '& > *': {
    gridColumn: '1 / 4'
  },

  [theme.breakpoints.up('md')]: {
    // first name field
    '& > :nth-child(1)': {
      gridColumn: '1'
    },

    // last name field
    '& > :nth-child(2)': {
      gridColumn: '3'
    },

    // job title field
    '& > :nth-child(3)': {
      gridColumn: '1'
    },

    // phone field
    '& > :nth-child(4)': {
      gridColumn: '3'
    },

    // city field
    '& > :nth-child(8)': {
      gridColumn: '1'
    },

    // state field
    '& > :nth-child(9)': {
      gridColumn: '3'
    }
  }
}))

const Actions = styled('div')({
  marginTop: 24,
  display: 'flex',
  justifyContent: 'space-between'
})

const StyledLegal = styled(Legal)({
  flex: '0 1 auto'
})

const SignUpButton = styled(Button)({
  flex: '0 0 auto',
  marginLeft: 20
})

const AlreadyRegistered = styled('p')(({theme}) => ({
  color: theme.palette.error.main,
  margin: 0,
  padding: 5
}))

const AlreadyRegisteredLink = styled(Link)({
  color: '#0000EE',
  textDecoration: 'underline',
  '&:hover': {
    cursor: 'pointer'
  }
})

const Signup = ({props, ...rest}) => {
  const {status: regStatus, errors: regErrors} = useRegistrationState()
  const [state, dispatch] = useReducer(reducer, {}, initializeState)
  const {user, errors, showErrors} = state

  const dispatchRedux = useDispatch()

  const handleSubmit = event => {
    event.preventDefault()
    if (Object.keys(errors).length > 0) {
      fields.forEach(f => dispatch(showError(f.key)))
      dispatch(showError('organizationTypeExplanation'))
    } else {
      dispatchRedux(createUser({...user, isVerified: true}, true))
    }
  }
  const isEmailAlreadyRegistered =
    regStatus === 'conflict' && regErrors && regErrors.email === 'value'
  return (
    <Content {...props}>
      <Title>Sign up</Title>
      <Subtitle>Get started with your free account</Subtitle>
      <Form onSubmit={handleSubmit}>
        {fields.map(({key, label, type, required, autocomplete}, i) => {
          const autoFocus = i === 0
          const defaultValue = user[key]
          let hasError = !!errors[key] && showErrors.includes(key)
          let errorMessage = errors[key]

          if (
            key === 'email' &&
            regStatus === 'conflict' &&
            defaultValue === regErrors.email
          ) {
            hasError = true
            errorMessage =
              errors[key] || 'That email address has already been registered'
          }

          const handleChange = e => dispatch(updateField(key, e.target.value))
          const handleBlur = () => {
            if (Object.keys(user).length > 0) dispatch(showError(key))
          }

          if (type === 'countrySelect') {
            return (
              <CountrySelect
                key={key}
                value={defaultValue}
                onChange={handleChange}
                error={hasError}
                errorMessage={errorMessage}
              />
            )
          }

          if (type === 'purchaseLearningSystem') {
            return (
              <MuiFormControl
                key={key}
                variant='outlined'
                margin='dense'
                error={hasError}
                required={required}
              >
                <MuiInputLabel htmlFor='purchaseLearningSystem'>
                  {label}
                </MuiInputLabel>
                <MuiSelect
                  name='purchaseLearningSystem'
                  value={defaultValue || ''}
                  onChange={handleChange}
                  labelWidth={500}
                >                  
                  <MuiMenuItem value={'0-3'}>0-3 months</MuiMenuItem>
                  <MuiMenuItem value={'3-6'}>3-6 months</MuiMenuItem>
                  <MuiMenuItem value={'6-12'}>6-12 months</MuiMenuItem>
                  <MuiMenuItem value={'12+'}>12+ months</MuiMenuItem>
                  <MuiMenuItem value={'no'}>Not interested</MuiMenuItem>
                </MuiSelect>
              </MuiFormControl>
            )
          }
          if (type === 'numberOfUsersToSupport') {
            return (
              <MuiFormControl
                key={key}
                variant='outlined'
                margin='dense'
                error={hasError}
                required={required}
              >
                <MuiInputLabel htmlFor='numberOfUsersToSupport'>
                  {label}
                </MuiInputLabel>
                <MuiSelect
                  name='numberOfUsersToSupport'
                  value={defaultValue || ''}
                  onChange={handleChange}
                  labelWidth={500}
                >                  
                  <MuiMenuItem value={'50-999'}>50 - 999 users</MuiMenuItem>
                  <MuiMenuItem value={'1000-4999'}>1,000 - 4,999 users</MuiMenuItem>
                  <MuiMenuItem value={'5000-9999'}>5,000 - 9,999 users</MuiMenuItem>
                  <MuiMenuItem value={'10000-49999'}>10,000 - 49,999 users</MuiMenuItem>
                  <MuiMenuItem value={'50000-199999'}>50,000 - 199,999 users</MuiMenuItem>
                  <MuiMenuItem value={'200000-999999'}>200,000 - 999,999 users</MuiMenuItem>
                  <MuiMenuItem value={'1000000+'}>1,000,000+ users</MuiMenuItem>
                  
                </MuiSelect>
              </MuiFormControl>
            )
          }
          if (type === 'organizationType') {
            const hasSelectedOther = defaultValue === 'other'
            const hasSelectedSchool = defaultValue === 'universityCollege' || defaultValue === 'primarySecondaryEducation'
            const handleExplanationChange = e =>
              dispatch(updateField('organizationTypeExplanation', e.target.value))
            const handleRoleChange = e =>
              dispatch(updateField('organizationTypeRole', e.target.value))
            const explanationValue = user['organizationTypeExplanation'] || ''
            const roleValue = user['organizationTypeRole'] || ''
            return (
              <MuiFormControl
                key={key}
                variant='outlined'
                margin='dense'
                required={required}
                error={hasError}
              >
                <MuiInputLabel htmlFor='organizationType'>{label}</MuiInputLabel>
                <MuiSelect
                  name='organizationType'
                  value={defaultValue || ''}
                  onChange={handleChange}
                  labelWidth={175}
                >
                  <MuiMenuItem value={'smallMidSize'}>Small Business/Mid-Size</MuiMenuItem>
                  <MuiMenuItem value={'enterprise'}>Enterprise</MuiMenuItem>
                  <MuiMenuItem value={'largeEnterprise'}>Large Enterprise</MuiMenuItem>
                  <MuiMenuItem value={'government'}>Government</MuiMenuItem>
                  <MuiMenuItem value={'customerTraining'}>Customer Training/Partner Enablement</MuiMenuItem>
                  <MuiMenuItem value={'universityCollege'}>University/College</MuiMenuItem>
                  <MuiMenuItem value={'primarySecondaryEducation'}>Primary/Secondary Education</MuiMenuItem>
                  <MuiMenuItem value={'association'}>Association</MuiMenuItem>
                  <MuiMenuItem value={'nonProfit'}>Non-Profit</MuiMenuItem>
                  <MuiMenuItem value={'other'}>Other</MuiMenuItem>
                </MuiSelect>
                {hasError && <FormHelperText>{errorMessage}</FormHelperText>}
                {hasSelectedOther && (
                  <TextField
                    label={'Please describe your organization type'}
                    type='text'
                    defaultValue={explanationValue}
                    onChange={handleExplanationChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    margin='dense'
                    fullWidth
                    required
                    error={!!errors['organizationTypeExplanation'] && showErrors.includes('organizationTypeExplanation')}
                    helperText={!!errors['organizationTypeExplanation'] && showErrors.includes('organizationTypeExplanation') && errors['organizationTypeExplanation']}
                  />
                )}
                {hasSelectedSchool && (
                  <MuiFormControl variant='outlined' margin='dense' required>
                    <MuiInputLabel htmlFor='organizationTypeRole'>Role</MuiInputLabel>
                    <MuiSelect
                      name='organizationTypeRole'
                      value={roleValue}
                      onChange={handleRoleChange}
                      labelWidth={40}
                    >
                      <MuiMenuItem value={'administration'}>Administration</MuiMenuItem>
                      <MuiMenuItem value={'student'}>Student</MuiMenuItem>
                    </MuiSelect>
                  </MuiFormControl>
                )}
              </MuiFormControl>
            )
          }

          if (type === 'usageType') {
            const hasSelectedOther = defaultValue === 'other'
            const handleOtherInputChange = e =>
              dispatch(updateField('usageTypeExplanation', e.target.value))
            const otherInputValue = user['usageTypeExplanation'] || ''
            return (
              <MuiFormControl
                key={key}
                variant='outlined'
                margin='dense'
                required={required}
                error={hasError}
              >
                <MuiInputLabel htmlFor='usageType'>{label}</MuiInputLabel>
                <MuiSelect
                  name='usageType'
                  value={defaultValue || ''}
                  onChange={handleChange}
                  labelWidth={275}
                >
                  <MuiMenuItem value={'justLooking'}>Just looking</MuiMenuItem>
                  <MuiMenuItem value={'researchingSystems'}>
                    Researching system(s) to buy
                  </MuiMenuItem>
                  <MuiMenuItem value={'identifyingSystems'}>
                    Identifying system(s) of interest
                  </MuiMenuItem>
                  <MuiMenuItem value={'competitiveIntel'}>
                    Competitive Intel
                  </MuiMenuItem>
                  <MuiMenuItem value={'other'}>Other</MuiMenuItem>
                </MuiSelect>
                {hasError && <FormHelperText>{errorMessage}</FormHelperText>}
                {hasSelectedOther && (
                  <TextField
                    key={key}
                    label={'Please explain how do you plan to use FindAnLMS'}
                    type={type}
                    defaultValue={otherInputValue}
                    onChange={handleOtherInputChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    margin='dense'
                    fullWidth
                    autoComplete={autocomplete}
                    error={hasError}
                    helperText={hasError && errorMessage}
                  />
                )}
              </MuiFormControl>
            )
          }
          /*
          if (type === 'contactConsent') {
            return (
              <MuiFormControl
                key={key}
                variant='outlined'
                margin='dense'
                error={hasError}
                required={required}
              >
                <MuiInputLabel htmlFor='contactConsent'>{label}</MuiInputLabel>
                <MuiSelect
                  name='contactConsent'
                  value={defaultValue || ''}
                  onChange={handleChange}
                  labelWidth={300}
                  error={hasError}
                >
                  <MuiMenuItem value={'no'}>No</MuiMenuItem>
                  <MuiMenuItem value={'yes'}>Yes</MuiMenuItem>
                </MuiSelect>
              </MuiFormControl>
            )
          }
          */
          return (
            <TextField
              key={key}
              label={label}
              type={type}
              defaultValue={defaultValue}
              onChange={handleChange}
              onBlur={handleBlur}
              variant='outlined'
              margin='dense'
              fullWidth
              required={required}
              autoComplete={autocomplete}
              error={hasError}
              helperText={hasError && errorMessage}
              autoFocus={autoFocus}
            />
          )
        })}
      </Form>
      {isEmailAlreadyRegistered && (
        <AlreadyRegistered>
          Already registered. Please{' '}
          <AlreadyRegisteredLink to="/login">
            login
          </AlreadyRegisteredLink>
          . If you forgotten your password, you can reset it{' '}
          <AlreadyRegisteredLink to="/password">
            here
          </AlreadyRegisteredLink>
          .
        </AlreadyRegistered>
      )}
      <Actions>
        <StyledLegal />
        <SignUpButton
          onClick={handleSubmit}
          color='primary'
          variant='contained'
        >
          Sign Up
        </SignUpButton>
      </Actions>
    </Content>
  )
}

export default Signup
